// src/components/sops/SopsPage.js — sopweb-bdb7afcd (SOPs Library, Seam 2) · sopread-7e4c2a93 (Seam 3)
// Everything under /sops. CrmApp renders this when utils/sopsUi.showSopsMenu is true (switch exactly on
// AND a role that can open SOPs, or god mode); the server and firestore.rules decide every read anyway.
//   EVERYONE:          /sops (To read), /sops/all (All my SOPs)
//   Manager and up:    /sops/library (Manage library), /sops/new, /sops/:id (details), /sops/:id/edit
//                      — anyone else is sent to /sops (showSopsLibrary, the same check as Seam 2).
// READS: the person's own access records — ONE query (sopsApi.loadMyAccess), for the tab counts and
// My SOPs; the library and details pages read as in Seam 2.
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, useLocation } from 'react-router-dom';
import SopsLibrary from './SopsLibrary';
import SopEditorPage from './SopEditorPage';
import SopDetailsPage from './SopDetailsPage';
import MySops from './MySops';
import { loadMyAccess, friendlyError } from './sopsApi';
import { SOPS_PATHS, SOP_OPEN_PARAM, allMyRows, showSopReaders, showSopsLibrary, toReadCount } from '../../utils/sopsUi';

const ID_RE = /^[A-Za-z0-9_-]{1,128}$/;
const RESERVED = new Set(['new', 'all', 'library']);

function TabBar({ toRead, all, isManager }) {
    const tabs = [
        { to: SOPS_PATHS.toRead, label: `To read (${toRead})` },
        { to: SOPS_PATHS.all, label: `All my SOPs (${all})` },
        ...(isManager ? [{ to: SOPS_PATHS.library, label: 'Manage library' }] : []),
    ];
    return (
        <div className="flex overflow-x-auto border-b" role="tablist">
            {tabs.map((t) => (
                <NavLink key={t.to} to={t.to} end role="tab"
                    className={({ isActive }) => `-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm ${isActive ? 'border-[#5F4EC9] font-medium text-[#5F4EC9]' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
                    {t.label}
                </NavLink>
            ))}
        </div>
    );
}

export default function SopsPage({ currentUserProfile, users, companySettings }) {
    const location = useLocation();
    const orgId = currentUserProfile?.organizationId;
    const uid = currentUserProfile?.id || currentUserProfile?.uid || null;
    const isManager = showSopsLibrary(currentUserProfile, companySettings);
    const parts = location.pathname.split('/').filter(Boolean); // ['sops', ...]
    // sopremind-9c4e7a21 — /sops?open=<sopId> (bells, emails): My SOPs selects that SOP.
    const openParam = new URLSearchParams((location && location.search) || '').get(SOP_OPEN_PARAM);
    const openId = openParam && ID_RE.test(openParam) ? openParam : null;
    const tabKey = parts.length === 1 ? 'toRead' : parts.length === 2 && parts[1] === 'all' ? 'all' : parts.length === 2 && parts[1] === 'library' ? 'library' : null;

    const [mine, setMine] = useState({ status: 'loading', records: [], error: '' });
    const reload = useCallback(async () => {
        if (!orgId || !uid) return;
        try {
            setMine({ status: 'done', records: await loadMyAccess(orgId, uid), error: '' });
        } catch (e) {
            console.warn('sopread: my SOPs load failed', e);
            setMine((m) => ({ ...m, status: 'error', error: friendlyError(e, 'Your SOPs couldn’t be loaded. Please try again.') }));
        }
    }, [orgId, uid]);
    // Only the tab pages need the person's records (details / edit / new don't).
    useEffect(() => { if (tabKey) reload(); }, [reload, tabKey]);

    if (!orgId) return null;
    const tabBar = <TabBar toRead={toReadCount(mine.records)} all={allMyRows(mine.records).length} isManager={isManager} />;
    if (tabKey === 'toRead' || tabKey === 'all') {
        return <MySops tab={tabKey} tabBar={tabBar} records={mine.records} status={mine.status} error={mine.error} reload={reload}
            accountName={currentUserProfile?.name || ''} timeZone={companySettings?.timezone} openId={openId}
            showReaders={showSopReaders(currentUserProfile)} />; // leadhand-9cb21d86 — Lead Hand: "Who's read it"
    }
    // Everything below is for Manager and up.
    if (!isManager) return <Navigate to={SOPS_PATHS.toRead} replace />;
    if (tabKey === 'library') return <SopsLibrary orgId={orgId} tabBar={tabBar} />;
    if (parts[1] === 'new' && parts.length === 2) {
        return <SopEditorPage key="new" orgId={orgId} mode="new" users={users} initialFolderId={location.state?.folderId || null} />;
    }
    const sopId = parts[1];
    if (!ID_RE.test(sopId) || RESERVED.has(sopId)) return <Navigate to={SOPS_PATHS.library} replace />;
    if (parts[2] === 'edit' && parts.length === 3) return <SopEditorPage key={`edit-${sopId}`} orgId={orgId} mode="edit" sopId={sopId} users={users} />;
    if (parts.length === 2) return <SopDetailsPage key={sopId} orgId={orgId} sopId={sopId} users={users} timeZone={companySettings?.timezone} />;
    return <Navigate to={SOPS_PATHS.library} replace />;
}
